import { useMemo, useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useAllRecipeIngredients, useCostSummary, useRecipeStages } from "@/hooks/recipes";
import type { Recipe } from "@/types/recipe";
import type { RecipeStep } from "@/components/recipes/execution-steps";
import {
  buildStagedMethod,
  numberedSteps,
  scaledYield,
  type BatchScale,
} from "@/lib/recipes/staged-method";
import { RecipeMethodHeader } from "./RecipeMethodHeader";
import { RecipeStagedSheet } from "./RecipeStagedSheet";
import { RecipeSimpleCard } from "./RecipeSimpleCard";
import { RecipeIngredientMeasures } from "./RecipeIngredientMeasures";
import { RecipeStagesEditorDialog } from "./RecipeStagesEditorDialog";

/** The simple card, or the staged sheet with every step written out beside its ingredients. */
export type RecipeMethodLayout = "simple" | "staged";

interface RecipeMethodViewProps {
  recipe: Recipe;
  layout: RecipeMethodLayout;
  canEdit: boolean;
}

/**
 * The recipe card views built on the method's stages (design section 8). Loads
 * the stages and every line, lets the cook pick a batch size, opens the stage
 * editor for people who can change the recipe, and keeps the expanded
 * ingredient conversions under either card.
 */
export function RecipeMethodView({ recipe, layout, canEdit }: RecipeMethodViewProps) {
  const { t } = useLingui();
  const [scale, setScale] = useState<BatchScale>(1);
  const [editorOpen, setEditorOpen] = useState(false);
  const stagesQuery = useRecipeStages(recipe.id);
  const linesQuery = useAllRecipeIngredients(recipe.id);
  // The cost summary carries each line's weight, volume, density and yield.
  // It is extra detail: the card draws without it and fills in when it lands.
  const { data: costSummary } = useCostSummary(recipe.id);
  const measures = useMemo(
    () => new Map(costSummary?.lines.map((line) => [line.ingredientId, line.measures ?? null])),
    [costSummary],
  );
  const stages = stagesQuery.data;
  const linesPage = linesQuery.data;

  const steps: RecipeStep[] = Array.isArray(recipe.instructions) ? (recipe.instructions as RecipeStep[]) : [];
  const lines = linesPage?.data ?? [];
  const yieldCount = scaledYield(recipe.yieldQuantity, scale);

  return (
    <article data-print-region aria-label={t`Recipe card`} className="mx-6 mb-10 flex flex-col gap-5 print:m-0">
      <RecipeMethodHeader
        recipe={recipe}
        scale={scale}
        onScaleChange={setScale}
        onEditStages={canEdit ? () => setEditorOpen(true) : undefined}
      />

      {stagesQuery.isError || linesQuery.isError ? (
        // A failed load is said as such: neither a skeleton that never ends nor
        // a sheet that looks like a recipe with no ingredients.
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-(--color-danger-bg) px-4 py-3 text-sm text-(--color-danger-text)">
          <span>{t`This recipe's method couldn't be loaded.`}</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              if (stagesQuery.isError) void stagesQuery.refetch();
              if (linesQuery.isError) void linesQuery.refetch();
            }}
          >
            {t`Try again`}
          </Button>
        </div>
      ) : !stages || !linesPage ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <>
          {!stages.some((stage) => stage.kind === "STAGE") && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-(--color-surface-muted) px-4 py-3 text-sm text-(--color-text-secondary) print:hidden">
              <span>{t`This recipe's method isn't split into stages yet, so everything is shown as one group.`}</span>
              {canEdit && (
                <Button type="button" size="sm" onClick={() => setEditorOpen(true)}>
                  {t`Split into stages`}
                </Button>
              )}
            </div>
          )}
          {layout === "staged" ? (
            <RecipeStagedSheet
              method={buildStagedMethod(stages, lines, steps)}
              scale={scale}
              yieldQuantity={recipe.yieldQuantity}
              measures={measures}
              recipeId={recipe.id}
            />
          ) : (
            <RecipeSimpleCard
              method={buildStagedMethod(stages, lines, steps)}
              scale={scale}
              yieldCount={yieldCount}
              measures={measures}
              recipeId={recipe.id}
            />
          )}
          {/* Open by default beside the full method, folded away on the simple card. */}
          <RecipeIngredientMeasures
            key={layout}
            lines={costSummary?.lines}
            scale={scale}
            defaultOpen={layout === "staged"}
          />
          {canEdit && (
            <RecipeStagesEditorDialog
              open={editorOpen}
              onOpenChange={setEditorOpen}
              recipeId={recipe.id}
              stages={stages}
              lines={lines}
              steps={numberedSteps(steps)}
            />
          )}
        </>
      )}
    </article>
  );
}
